/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import Styles from '../../css/ChangeContactInfo.module.css';
import { Button } from '@mui/material';
import useAbortController from '../../common/hooks/useAbortController';
import axiosCall from '../../services/axios';
import ButtonBorder from '../ButtonBorder';
import LoadingModal from '../../components/modals/LoadingModal';
import { getFormattedPhoneNumber, getFormattedPhoneExt } from '../../common/UtilityFunctions';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useBrwrContext } from '../../contexts/BorrowerContext';
import useApplicationList from '../../common/hooks/useApplicationList';
import ButtonBorderWrapper from '../../components/ButtonBorderWrapper';
import EnvelopeWithBox from '../../components/EnvelopeWithBox';
import { useSnackbar } from 'notistack';
import { useViewingContext } from '../../contexts/ViewingContext';

const ChangeContactInfoForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { reset } = useAbortController();
  const { token, servicerUser } = useAuthContext();
  const { loanNumber, refetchLoanData } = useLoanContext();
  const { borrowerData, reloadBorrowerData } = useBrwrContext();
  const { applicationList } = useApplicationList('borrowerNumber,coBorrowerNumber');
  const { isServicerView } = useViewingContext();

  // state variables for fields
  const [hasChanges, setHasChanges] = useState(false);
  const [primaryBorrowerIndex, setPrimaryBorrowerIndex] = useState(-1);
  const [defaultMailingAddress, setDefaultMailingAddress] = useState(false);
  const [checkboxLoading, setCheckboxLoading] = useState(false);
  const [mailingData, setMailingData] = useState({
    isLoading: true,
    mailingState: '',
    mailingAddress: '',
    mailingUnitNumber: '',
    mailingCity: '',
    mailingZipCode: '',
  });
  const [propData, setPropData] = useState({
    isLoading: true,
    address: '',
    unitNumber: '',
    city: '',
    state: '',
    zipCodeFormatted: '',
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [mailingCityOptions, setMailingCityOptions] = useState([]);

  type BorrowerDataWithNumber = (typeof borrowerData)['01'] & {
    borrowerNumber: string;
    productCode?: string;
    counselingComp?: string;
  };

  const [dynamicBorrowers, setDynamicBorrowers] = useState<BorrowerDataWithNumber[]>([]);
  const [initialDynamicBorrowers, setInitialDynamicBorrowers] = useState<BorrowerDataWithNumber[] | null>(null);
  const [nbps, setNbps] = useState<BorrowerDataWithNumber[]>([]);

  const getNumberToDisplay = useCallback(
    (borrower: BorrowerDataWithNumber): string => {
      if (borrower.borrowerOnNote) return borrower.borrowerNumber;
      return (nbps.findIndex((b) => b.borrowerNumber == borrower.borrowerNumber) + 1).toString();
    },
    [nbps],
  );

  // load borrower data from borrower list endpoint and application list endpoint
  useEffect(() => {
    const fetchBorrowersInApplicationOrder = async () => {
      try {
        const borrowerList = Object.values(borrowerData);
        const fullList: BorrowerDataWithNumber[] = borrowerList.map((b: any) => ({
          ...b,
          borrowerNumber: b.borrowerNumber,
        }));

        const borrowerByNumber: Record<string, BorrowerDataWithNumber> = {};
        fullList.forEach((b) => {
          borrowerByNumber[b.borrowerNumber] = b;
        });

        // order borrowers based on borrower number
        const orderedNumbers: string[] = [];
        applicationList.forEach((app: any) => {
          const { borrowerNumber: main, coBorrowerNumber: co } = app;
          if (main && !orderedNumbers.includes(main)) orderedNumbers.push(main);
          if (co && co.trim() !== '' && !orderedNumbers.includes(co)) orderedNumbers.push(co);
        });

        const orderedBorrowers = orderedNumbers.map((num) => borrowerByNumber[num]).filter(Boolean);
        const leftoverBorrowers = fullList
          .filter((b) => !orderedNumbers.includes(b.borrowerNumber))
          .filter((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED')
          .map((b) => borrowerByNumber[b.borrowerNumber]);

        const nbp = fullList.filter((b) => !b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED');
        setNbps(nbp);
        const finalBorrowerOrder = [...orderedBorrowers, ...leftoverBorrowers, ...nbp].filter(
          (b) => b.borrowerTypeKeyword !== 'REMOVED',
        );

        // set the primary borrower to be the first borrower in the ordered list that is on the note and not removed
        setPrimaryBorrowerIndex(
          finalBorrowerOrder.findIndex((b) => b.borrowerOnNote && b.borrowerTypeKeyword !== 'REMOVED') || -1,
        );
        setDynamicBorrowers(finalBorrowerOrder);
        if (!initialDynamicBorrowers) {
          setInitialDynamicBorrowers(finalBorrowerOrder);
        }
      } catch (error) {
        console.error('Error fetching borrowers:', error);
        setDynamicBorrowers([]);
        setNbps([]);
      }
    };
    if (loanNumber && token) {
      fetchBorrowersInApplicationOrder();
    }
  }, [borrowerData, applicationList, loanNumber, token, reset]);

  function handleAddressChange(e) {
    setMailingData({
      ...mailingData,
      mailingAddress: e.target.value,
    });
  }

  function handleStateChange(e) {
    setMailingData({
      ...mailingData,
      mailingState: e.target.value,
    });
  }

  function handleCityChange(e) {
    setMailingData({
      ...mailingData,
      mailingCity: e.target.value,
    });
  }

  function handleUnitNumberChange(e) {
    setMailingData({
      ...mailingData,
      mailingUnitNumber: e.target.value,
    });
  }

  function handleZipChange(e) {
    setMailingData({
      ...mailingData,
      mailingZipCode: e.target.value,
    });
  }

  // Handler for default checkbox
  const handleDefaultToggle = useCallback(async () => {
    try {
      if (!token || !loanNumber) {
        return;
      }
      // set all defaults to opposite of current value
      const useDefaults = !defaultMailingAddress;
      setCheckboxLoading(true);
      const url = `live/loan/${loanNumber}`;
      const body = {
        defaultMailingAddress: useDefaults,
        defaultMailingCity: useDefaults,
        defaultMailingState: useDefaults,
        defaultMailingUnitNumber: useDefaults,
        defaultMailingZipCode: useDefaults,
      };
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall({ url: url, method: 'post', data: body }, undefined, headers);
      if (!response) throw new Error('Error when changing mailing checkbox');
      // update state variable
      setDefaultMailingAddress(useDefaults);
      setMailingData(response);
      refetchLoanData();
    } catch (error) {
      setCheckboxLoading(false);
      console.error(`encountered an error setting mailing checkbox: ${error.message}`);
    }
    setCheckboxLoading(false);
  }, [defaultMailingAddress, token, loanNumber]);

  // Pulling data from LoanEndpoint for mailing fields
  const getLoanData = useCallback(async () => {
    try {
      if (!token || !loanNumber) {
        return;
      }
      const request = {
        url: `live/loan/${loanNumber}`,
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response || !response.loanNumber) throw new Error(`Invalid response for loan number ${loanNumber}`);
      setMailingData(response);
      if (response.defaultMailingAddress) {
        setDefaultMailingAddress(response.defaultMailingAddress);
      }
    } catch (error) {
      console.error(`LoanInformation.getLoanData encountered an error: ${error.message}`);
      setMailingData((prev) => ({ ...prev, isLoading: false }));
    }
  }, [loanNumber, token, reset]);
  useEffect(() => {
    getLoanData();
  }, [getLoanData]);

  const handleDynamicBorrowerChange = (borrowerNumber: string, field: string) => (e) => {
    const tempValue =
      e.target.value == null ? '' : typeof e.target.value === 'string' ? e.target.value.trim() : e.target.value;
    const newValue = ['true', 'false'].includes(tempValue) ? tempValue === 'true' : tempValue;

    // Set Consent to approved when borrower updates phone contact information
    if (!servicerUser && field === 'homePhoneNumber' && newValue !== '') {
      setDynamicBorrowers((prev) =>
        prev.map((b) => {
          if (b.borrowerNumber !== borrowerNumber) return b;
          return { ...b, homePhoneBorrowerApproved: true };
        }),
      );
    }

    if (!servicerUser && field === 'mobilePhoneNumber' && newValue !== '') {
      setDynamicBorrowers((prev) =>
        prev.map((b) => {
          if (b.borrowerNumber !== borrowerNumber) return b;
          return { ...b, mobilePhoneBorrowerApproved: true };
        }),
      );
    }

    if (!servicerUser && field === 'workPhoneNumber' && newValue !== '') {
      setDynamicBorrowers((prev) =>
        prev.map((b) => {
          if (b.borrowerNumber !== borrowerNumber) return b;
          return { ...b, workPhoneBorrowerApproved: true };
        }),
      );
    }

    setDynamicBorrowers((prev) =>
      prev.map((b) => {
        if (b.borrowerNumber !== borrowerNumber) return b;
        return { ...b, [field]: newValue };
      }),
    );
    setHasChanges(true);
  };

  // Pulling data from PropertyEndpoint
  const getPropData = useCallback(async () => {
    try {
      if (!token || !loanNumber) {
        return;
      }
      const request = {
        url: `live/loan/${loanNumber}/property/001`,
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (!response || !response.address) throw new Error(`Invalid response for loan number ${loanNumber}`);
      setPropData(response);
    } catch (error) {
      console.error(`LoanInformation.getPropData encountered an error: ${error.message}`);
      setPropData((prev) => ({ ...prev, isLoading: false, address: 'Property Not Found' }));
    }
  }, [loanNumber, token, reset]);
  useEffect(() => {
    getPropData();
  }, [getPropData]);

  const getBorrowerEndpoints = () => {
    if (!initialDynamicBorrowers) return [];
    return dynamicBorrowers.map((b) => {
      const { borrowerNumber } = b;
      const payload = {
        emailAddress: b.emailAddress,
        homePhoneNumber: b.homePhoneNumber.replace(/\D/g, ''),
        mobilePhoneNumber: b.mobilePhoneNumber.replace(/\D/g, ''),
        workPhoneNumber: b.workPhoneNumber.replace(/\D/g, ''),
        workExt: b.workExt,
        homePhoneBorrowerApproved: b.homePhoneBorrowerApproved,
        mobilePhoneBorrowerApproved: b.mobilePhoneBorrowerApproved,
        workPhoneBorrowerApproved: b.workPhoneBorrowerApproved,
      };
      const init = (initialDynamicBorrowers.find((ib) => ib.borrowerNumber === borrowerNumber) ||
        {}) as BorrowerDataWithNumber;
      const initialPayload = {
        homePhoneNumber: init.homePhoneNumber.replace(/\D/g, ''),
        mobilePhoneNumber: init.mobilePhoneNumber.replace(/\D/g, ''),
        workPhoneNumber: init.workPhoneNumber.replace(/\D/g, ''),
        workExt: init.workExt,
        homePhoneBorrowerApproved: init.homePhoneBorrowerApproved,
        mobilePhoneBorrowerApproved: init.mobilePhoneBorrowerApproved,
        workPhoneBorrowerApproved: init.workPhoneBorrowerApproved,
      };
      return {
        url: `live/loan/${loanNumber}/borrower/${borrowerNumber}`,
        data: payload,
        initial: initialPayload,
      };
    });
  };

  const getUpdatePromises = useCallback(
    ({
      endpoints,
      token,
      reset,
    }: {
      endpoints: Array<{ data: any; initial: any; url: string }>;
      token: string;
      reset: () => any;
    }): Promise<any>[] => {
      try {
        const headers = { 'Session-Token': token };
        const updatePromises: Promise<any>[] = [];

        endpoints.forEach(({ data, url }) => {
          const request = {
            url,
            method: 'post',
            data,
          };
          updatePromises.push(axiosCall(request, reset(), headers));
        });
        return updatePromises;
      } catch (error) {
        console.error('Error in getUpdatePromises:', error);
        return [];
      }
    },
    [reset, token],
  );

  // function for handling form submission
  const handleSubmit = useCallback(async () => {
    setSubmitLoading(true);
    const headers = {
      'Session-Token': token,
    };
    try {
      if (!token || !loanNumber) {
        return;
      }

      //borrower data section
      const borrowerEndpoints = getBorrowerEndpoints();
      const endpoints = [...borrowerEndpoints];
      const updatePromises = getUpdatePromises({ endpoints, token, reset });
      await Promise.allSettled(updatePromises);

      setInitialDynamicBorrowers([...dynamicBorrowers]);
      setDynamicBorrowers([...dynamicBorrowers]);
      setHasChanges(false);
      reloadBorrowerData();

      // POST updated mailing address data
      const url = `live/loan/${loanNumber}`;
      const body = {
        mailingAddress: mailingData.mailingAddress,
        mailingCity: mailingData.mailingCity,
        mailingState: mailingData.mailingState,
        mailingUnitNumber: mailingData.mailingUnitNumber,
        mailingZipCode: mailingData.mailingZipCode,
      };
      const response = await axiosCall({ url: url, method: 'post', data: body }, undefined, headers);
      if (!response) throw new Error(`Unable to submit form for loan number ${loanNumber}`);
      enqueueSnackbar('Information saved successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error when submitting: ' + error.message, { variant: 'error' });
      console.error(`encountered an error submitting borrower data: ${error.message}`);
      setSubmitLoading(false);
    }

    setSubmitLoading(false);
  }, [dynamicBorrowers, mailingData, token, loanNumber, enqueueSnackbar]);

  // Extract mailingZipCode from mailingData to prevent loops cuased by setting the same state from dependency array
  const mailingZipCode = useMemo(() => mailingData.mailingZipCode || '', [mailingData]);
  // Set city and state for mailing data when a valid zipcode is entered
  const getZipData = useCallback(async () => {
    if (!mailingZipCode || mailingZipCode.length !== 5) return;

    const url = 'public/zipcode/' + mailingZipCode;
    const headers = {
      'Session-Token': token,
    };
    const config = reset();

    try {
      const response = await axiosCall({ url: url, method: 'get' }, config, headers);
      if (!response) throw new Error(`Invalid response for zip code ${mailingZipCode}`);
      setMailingData((prev) => ({
        ...prev,
        mailingState: response.state,
        // no more looping from setting mailing data and retriggering dependency
      }));
      setMailingCityOptions(response.cities);
    } catch (error) {
      console.info(`ChangeContact.getZipData encountered an error fetching zip code data: ${error.message}`);
    }
  }, [mailingZipCode, reset, token]); // mailingData no longer required!
  useEffect(() => {
    getZipData();
  }, [getZipData]);

  // Display loading modal while data is being fetched or sent
  if (borrowerData.isLoading || mailingData.isLoading || propData.isLoading || submitLoading || checkboxLoading) {
    return <LoadingModal open={true} />;
  }

  return (
    <ButtonBorderWrapper>
      <EnvelopeWithBox blockName={'Change Contact Info'}>
        <main>
          <form className={Styles.formContent}>
            <h3 className={Styles.infoLabel}>Please complete the following information:</h3>
            &nbsp;
            <section className={Styles.gridContainer}>
              {dynamicBorrowers.map((borrower, idx) => (
                <section>
                  <label className={Styles.fieldLabel}>
                    <h4>
                      {(borrower.borrowerOnNote ? 'Borrower ' : 'Non-Borrowing Party ') +
                        getNumberToDisplay(borrower) +
                        ' '}
                      Name
                    </h4>
                    <input
                      className={Styles.textInput}
                      type='text'
                      name='bname'
                      placeholder='Borrower Name'
                      value={borrower?.fullName}
                      disabled
                    />
                  </label>
                  <label className={Styles.fieldLabel}>
                    Home Phone Number
                    <input
                      className={Styles.textInput}
                      type='tel'
                      placeholder='Home Phone Number'
                      value={getFormattedPhoneNumber(borrower?.homePhoneNumber || '')}
                      onChange={handleDynamicBorrowerChange(
                        borrower?.borrowerNumber.replace(/\D/g, ''),
                        'homePhoneNumber',
                      )}
                    />
                  </label>
                  {isServicerView && (
                    <div className={Styles.radioContainer}>
                      <label className={Styles.radioLabel}>Consent</label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactHome' + borrower?.borrowerNumber}
                          value='true'
                          checked={borrower?.homePhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'homePhoneBorrowerApproved')}
                        ></input>
                        Yes
                      </label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactHome' + borrower?.borrowerNumber}
                          value='false'
                          checked={!borrower?.homePhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'homePhoneBorrowerApproved')}
                        ></input>
                        No
                      </label>
                    </div>
                  )}
                  <label className={Styles.fieldLabel}>
                    Mobile Phone Number
                    <input
                      className={Styles.textInput}
                      type='text'
                      placeholder='Mobile Phone Number'
                      value={getFormattedPhoneNumber(borrower?.mobilePhoneNumber || '')}
                      onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'mobilePhoneNumber')}
                    />
                  </label>
                  {isServicerView && (
                    <div className={Styles.radioContainer}>
                      <label className={Styles.radioLabel}>Consent</label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactMobile' + borrower?.borrowerNumber}
                          value='true'
                          checked={borrower?.mobilePhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(
                            borrower?.borrowerNumber,
                            'mobilePhoneBorrowerApproved',
                          )}
                        ></input>
                        Yes
                      </label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactMobile' + borrower?.borrowerNumber}
                          value='false'
                          checked={!borrower?.mobilePhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(
                            borrower?.borrowerNumber,
                            'mobilePhoneBorrowerApproved',
                          )}
                        ></input>
                        No
                      </label>
                    </div>
                  )}
                  <div className={Styles.inputSectionNumber}>
                    <label className={Styles.fieldLabel}>
                      Work Phone Number
                      <input
                        className={Styles.textInput}
                        type='tel'
                        placeholder='Work Phone Number'
                        value={getFormattedPhoneNumber(borrower?.workPhoneNumber || '')}
                        onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'workPhoneNumber')}
                      />
                    </label>
                  </div>
                  <div className={Styles.inputSectionExt}>
                    <label className={Styles.fieldLabel}>
                      Extension
                      <input
                        className={Styles.textInputExt}
                        type='tel'
                        placeholder='Ext.'
                        value={getFormattedPhoneExt(borrower?.workExt || '')}
                        onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'workExt')}
                      />
                    </label>
                  </div>
                  {isServicerView && (
                    <div className={Styles.radioContainer}>
                      <label className={Styles.radioLabel}>Consent</label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactWork' + borrower?.borrowerNumber}
                          value='true'
                          checked={borrower?.workPhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'workPhoneBorrowerApproved')}
                        ></input>
                        Yes
                      </label>
                      <label className={Styles.radioOptions}>
                        <input
                          type='radio'
                          name={'canContactWork' + borrower?.borrowerNumber}
                          value='false'
                          checked={!borrower?.workPhoneBorrowerApproved}
                          onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'workPhoneBorrowerApproved')}
                        ></input>
                        No
                      </label>
                    </div>
                  )}
                  <label className={Styles.fieldLabel}>
                    Email
                    <input
                      className={Styles.textInput}
                      type='text'
                      placeholder='Email'
                      value={borrower?.emailAddress}
                      onChange={handleDynamicBorrowerChange(borrower?.borrowerNumber, 'emailAddress')}
                    />
                  </label>
                </section>
              ))}
            </section>
            <br />
            <p>Property Address of Record:</p>
            <section className={Styles.gridContainer}>
              <section className={Styles.inputSection}>
                <label htmlFor='pAddressOne' className={Styles.fieldLabel}>
                  Address Line 1
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Address Line 1'
                  value={propData.address}
                  disabled
                />
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pAddressTwo' className={Styles.fieldLabel}>
                  Address Line 2
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Address Line 2'
                  value={propData.unitNumber}
                  disabled
                />
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  City/State/Zip
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='City/State/Zip'
                  value={propData.city + ', ' + propData.state + ' ' + propData.zipCodeFormatted}
                  disabled
                />
              </section>
            </section>
            <p>Mailing Address *</p>
            <section className={Styles.checkboxSection}>
              <input type='checkbox' checked={defaultMailingAddress} onChange={handleDefaultToggle} />
              <label className={Styles.fieldLabel}>Same as property address:</label>
            </section>
            <section className={Styles.gridContainer}>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  Address Line 1
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Address Line 1'
                  value={mailingData.mailingAddress}
                  onChange={handleAddressChange}
                  disabled={defaultMailingAddress}
                />
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  Address Line 2
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Address Line 2'
                  value={mailingData.mailingUnitNumber}
                  onChange={handleUnitNumberChange}
                  disabled={defaultMailingAddress}
                />
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  Mailing City
                </label>
                <select
                  className={Styles.textInput}
                  value={mailingData.mailingCity}
                  onChange={handleCityChange}
                  disabled={defaultMailingAddress}
                >
                  {mailingCityOptions.map((item, index) => (
                    <option key={index} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  Mailing State
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Mailing State'
                  value={mailingData.mailingState}
                  onChange={handleStateChange}
                  disabled={defaultMailingAddress}
                />
              </section>
              <section className={Styles.inputSection}>
                <label htmlFor='pCityStateZip' className={Styles.fieldLabel}>
                  Mailing Zip
                </label>
                <input
                  className={Styles.textInput}
                  type='text'
                  placeholder='Mailing Zip'
                  value={mailingData.mailingZipCode}
                  onChange={handleZipChange}
                  disabled={defaultMailingAddress}
                />
              </section>
            </section>
          </form>
        </main>
      </EnvelopeWithBox>
      <ButtonBorder>
        <Button variant='contained' type='submit' className={Styles.button} onClick={() => handleSubmit()}>
          Submit
        </Button>
      </ButtonBorder>
    </ButtonBorderWrapper>
  );
};

export default ChangeContactInfoForm;
